import { chakra } from '@chakra-ui/react'

import { Theme } from 'src/themes/types'
import { scaleWithFont } from 'src/components/TranscriptViewer/constants'

// Sizes at the default font size; each scales with the transcript font (`scaleWithFont`).
const ICON_SIZE = scaleWithFont(24)
const ICON_GLYPH_SIZE = scaleWithFont(16)
const NUMBER_SIZE = scaleWithFont(18)
const NUMBER_GAP = scaleWithFont(4)
const EASE = 'cubic-bezier(.2,.8,.2,1)'

// The paragraph (1121) marks itself with `data-group`, so hovering anywhere on it fans the numbers out.
const EXPANDED = '&[data-expanded="true"], &:has([data-number]:focus-visible)'
const GROUP_HOVER = '@media (hover: hover)'

const pageBackground = (theme: Theme) => theme.palette.transcript.appBackgroundColors[theme.appBackgroundColorIndex].colorValue

const focusRing = (color: string, radius: string) => ({
    borderRadius: radius,
    outline: '2px solid',
    outlineColor: color,
    outlineOffset: '2px',
})

const hiddenNumber = {
    opacity: 0,
    visibility: 'hidden',
    transform: 'translateY(-4px) scale(.6)',
    transition: 'opacity .12s ease, transform .14s ease, visibility 0s linear .14s, background-color .12s, color .12s',
}

const shownNumber = {
    opacity: 1,
    visibility: 'visible',
    transform: 'none',
    transition: 'opacity .18s ease, transform .24s cubic-bezier(.2,.9,.25,1.15), visibility 0s, background-color .12s, color .12s',
}

const hiddenIcon = {
    opacity: 0,
    visibility: 'hidden',
    transform: 'scale(.6)',
    pointerEvents: 'none',
    transition: 'opacity .12s ease, transform .16s ease, visibility 0s linear .16s',
}

export const Root = chakra('div', {
    baseStyle: {
        position: 'relative',
        width: ICON_SIZE,
        height: ICON_SIZE,
        [EXPANDED]: {
            '& [data-multi]': hiddenIcon,
            '& [data-number]': shownNumber,
        },
        [GROUP_HOVER]: {
            '[data-group]:hover &': {
                '& [data-multi]': hiddenIcon,
                '& [data-number]': shownNumber,
            },
        },
        '@media (prefers-reduced-motion: reduce)': {
            '& *': { transition: 'none !important' },
        },
    },
})

export const IconButton = chakra('button', {
    baseStyle: ({ theme }) => {
        const { color, hoverBackgroundColor, selectedContentColor } = theme.palette.inconsistencyIndicator

        return {
            position: 'absolute',
            inset: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: ICON_SIZE,
            height: ICON_SIZE,
            padding: 0,
            border: 0,
            borderRadius: scaleWithFont(6),
            backgroundColor: 'transparent',
            color,
            cursor: 'pointer',
            transition: `opacity .16s ease, transform .2s ${EASE}, background-color .12s, color .12s`,
            // A larger hit area than the icon's square.
            _after: { content: '""', position: 'absolute', inset: `${scaleWithFont(-6)} ${scaleWithFont(-8)}` },
            '& > svg': { width: ICON_GLYPH_SIZE, height: ICON_GLYPH_SIZE },
            _hover: { backgroundColor: hoverBackgroundColor },
            _pressed: { backgroundColor: color, color: selectedContentColor },
            _focusVisible: focusRing(color, scaleWithFont(6)),
            '@media (forced-colors: active)': { _pressed: { outline: '2px solid Highlight' } },
        }
    },
})

export const Badge = chakra('span', {
    baseStyle: ({ theme }) => {
        const { color, selectedContentColor } = theme.palette.inconsistencyIndicator

        return {
            position: 'absolute',
            top: scaleWithFont(-4),
            right: scaleWithFont(-6),
            minWidth: scaleWithFont(13),
            height: scaleWithFont(13),
            padding: `0 ${scaleWithFont(3)}`,
            borderRadius: '999px',
            backgroundColor: color,
            color: selectedContentColor,
            fontSize: scaleWithFont(9),
            lineHeight: scaleWithFont(13),
            fontWeight: 600,
            textAlign: 'center',
            fontVariantNumeric: 'tabular-nums',
            boxShadow: `0 0 0 2px ${pageBackground(theme)}`,
            '@media (forced-colors: active)': { forcedColorAdjust: 'none' },
        }
    },
})

// A column of numbered circles centred under the icon; it never blocks the icon itself.
export const Numbers = chakra('div', {
    baseStyle: {
        position: 'absolute',
        top: 0,
        left: `calc((${ICON_SIZE} - ${NUMBER_SIZE}) / 2)`,
        display: 'flex',
        flexDirection: 'column',
        gap: NUMBER_GAP,
        pointerEvents: 'none',
        '& > :nth-of-type(2)': { transitionDelay: '45ms, 45ms, 0s, 0s, 0s' },
        '& > :nth-of-type(3)': { transitionDelay: '90ms, 90ms, 0s, 0s, 0s' },
    },
})

export const NumberButton = chakra('button', {
    baseStyle: ({ theme }) => {
        const { color, hoverBackgroundColor, selectedContentColor } = theme.palette.inconsistencyIndicator

        return {
            position: 'relative',
            width: NUMBER_SIZE,
            height: NUMBER_SIZE,
            padding: 0,
            border: '1.25px solid',
            borderColor: color,
            borderRadius: '999px',
            backgroundColor: pageBackground(theme),
            color,
            fontFamily: 'inherit',
            fontSize: scaleWithFont(10),
            lineHeight: scaleWithFont(15),
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
            cursor: 'pointer',
            pointerEvents: 'auto',
            ...hiddenNumber,
            _after: { content: '""', position: 'absolute', inset: `${scaleWithFont(-2)} ${scaleWithFont(-10)}` },
            _hover: { backgroundColor: hoverBackgroundColor },
            _pressed: { backgroundColor: color, color: selectedContentColor },
            _focusVisible: focusRing(color, '999px'),
            '@media (forced-colors: active)': { _pressed: { outline: '2px solid Highlight' } },
        }
    },
})
